import { mapActions, mapMutations, mapGetters } from 'vuex'
import store from '@/store'
import { isPdvRoute } from '@/router/utils'
import { isUserAllowedStore, hasErpAccess } from '@/auth/utils'
import isElectron from 'is-electron'
import {
  PayBoxMissingSetupError,
  PayBoxInativeError,
  PayBoxUnknownError,
  PayBoxConnectionError,
} from '@/store/pages/pdv/pay-box-configuration'
import loadingOverlay from './loading-overlay'

const environment = window.env || 'local'
const isProd = environment === 'production'
const isElectronLocal = isElectron()

// Flag para controlar se as configurações já foram carregadas nesta sessão
let pdvConfigurationLoaded = false
// Flag para controlar se os dados do PDV já foram carregados
let pdvDataLoaded = false
let pdvConfigurationCheckPromise = null
// Ultimo carregamento terminou em 404: o caixa esta fechado e a tela de abertura ja esta no ar.
// Repetir a busca devolve o mesmo 404, entao o guard barra ate o caixa ser aberto.
let pdvCashBookClosed = false
// Carregamento dos dados do PDV em andamento. O layout e a página registram este mixin, e os
// dois `mounted` disparam antes do primeiro terminar. Com a promise compartilhada os dois
// esperam o mesmo carregamento em vez de repeti-lo.
let pdvDataLoading = null

const payboxConfiguration = {
  mixins: [loadingOverlay],
  data() {
    return {
      macAddressWorkstation: null,
      mxRedirectWhenMount: false,
    }
  },
  computed: {
    ...mapGetters('pages/pdv/payBoxConfiguration', [
      'hasGlobalFilters',
      'currentPayboxConfiguration',
      'tefAgentConfigPayload',
      'isPdvAtm',
      'isPdvInactive',
    ]),

    /**
     * Verifica se as configurações do PDV já foram carregadas
     * Considera carregado se tiver um ID de paybox configurado
     */
    isPdvConfigurationLoaded() {
      return pdvConfigurationLoaded && this.currentPayboxConfiguration?.id != null
    },

  },
  methods: {
    ...mapActions('pages/pdv/payBoxConfiguration', [
      'fetchPayBox',
      'fetchPayBoxByMacAddress',
      'checkPayBoxIsActiveByMacAddress',
      'checkCurrentPayBoxConfigAndFetch',
    ]),
    ...mapActions('pages/pdv', ['loadPdvData']),
    ...mapMutations('pages/pdv/payBoxConfiguration', ['SET_PAY_BOX_ID']),

    ...mapActions('pages/pdv/payBoxConfiguration', {
      stPayboxConfigurationCleanState: 'cleanState',
    }),
    ...mapActions('pages/pdv/payBox', {
      stPayboxCleanState: 'cleanState',
    }),

    ...mapActions('pages/pdv/atm', {
      stInitAtm: 'initAtm',
      stInitAtmBase: 'initAtmBase',
      stLoadAtmCatalogData: 'loadAtmCatalogData',
    }),

    async pageLoadPdv(showLoading, forceReload = false) {
      const pdvConfigurationAvailable = await this.checkPdvCurrentConfiguration()
      if (!pdvConfigurationAvailable) {
        return
      }

      console.debug(`Loading PDV data... (showLoading: ${showLoading}, forceReload: ${forceReload})`)
      // `pdvDataLoaded` e uma variavel de modulo, sem reatividade. Lida por uma computed o valor
      // fica em cache na primeira avaliacao e o guard nunca mais barra nada, entao lemos direto.
      if (pdvDataLoaded && !forceReload) {
        console.info('PDV data already loaded, skipping...')
        return
      }

      if (pdvCashBookClosed && !forceReload) {
        console.info('Cash book is closed, skipping...')
        // mesmo efeito do 404: sem isto o `mounted` do mixin segue para mxPdvRouteRedirect(),
        // que no ATM volta para a tela do ATM e nunca mostra a abertura do caixa.
        this.mxRedirectWhenMount = false
        this.mxPayBoxOpenRouteRedirect()
        return
      }

      // quem cria a promise trata o erro; os demais so param o proprio redirecionamento
      let isLoadOwner = false
      if (!pdvDataLoading) {
        isLoadOwner = true
        pdvDataLoading = this.runLoadPdvData().finally(() => {
          pdvDataLoading = null
        })
      }

      const loadPromise = pdvDataLoading

      try {
        if (showLoading) {
          this.showLoadingOverlay()
        }

        const error = await loadPromise
        if (error) {
          this.mxRedirectWhenMount = false

          if (!isLoadOwner) {
            return
          }

          if (error?.response?.status === 404) {
            this.mxPayBoxOpenRouteRedirect()
            // marcado depois do redirect: ele chama resetPdvConfigurationLoaded()
            pdvCashBookClosed = true
          } else {
            this.showError({ error })
            this.$router.push({ name: 'home' })
          }
        }
      } finally {
        if (showLoading) {
          this.hideLoadingOverlay()
        }
      }
    },

    /**
     * Carrega os dados do PDV. Devolve o erro em vez de lanca-lo, para que todas as chamadas
     * que compartilham a promise recebam o mesmo resultado.
     */
    async runLoadPdvData() {
      try {
        await this.checkCurrentPayBoxConfigAndFetch()

        if (this.$router.currentRoute.name !== 'pdv-pay-box-configuration') {
          console.debug('Loading PDV data...')
          await this.loadPdvData()
          pdvDataLoaded = true
          pdvCashBookClosed = false
        }
        return null
      } catch (error) {
        return error
      }
    },
    async loadPdvCurrentConfiguration(forceReload = false) {
      // Se já carregou e não é forçado, pula a execução
      if (this.isPdvConfigurationLoaded && !forceReload) {
        console.info('PDV configuration already loaded, skipping...')
        return
      }

      if (!isProd) {
        if (!localStorage.getItem('devOnlyUsePayboxIdSkip')) {
          const usePayBoxId = Number(localStorage.getItem('devOnlyUsePayboxId'))
          if (usePayBoxId) {
            console.warn(`Force using use-paybox-id: ${usePayBoxId}`)

            const currentId = Number(this.currentPayboxConfiguration.id)
            // após o reload o id é null; limpar aqui apagaria a venda salva no IndexedDB
            if (currentId && usePayBoxId !== currentId) {
              this.stPayboxConfigurationCleanState()
              this.stPayboxCleanState()
            }

            this.SET_PAY_BOX_ID(usePayBoxId)
            await this.fetchPayBox()
            pdvConfigurationLoaded = true
            return
          }
        }
      }

      if (!isElectronLocal) {
        return
      }
      const payboxData = await window.electronAPI.system.identifyPayboxRead()
      const systemInfo = await window.electronAPI.system.systemInfo()
      await this.fetchPayBoxByMacAddress({
        guid: payboxData?.guid,
        macAddress: systemInfo.macAddress,
      })
      pdvConfigurationLoaded = true
    },

    async mxPdvRouteRedirect() {
      if (this.isPdvAtm) {
        if (this.$router.currentRoute.name !== 'pdv-atm')
          this.$router.push({ name: 'pdv-atm-identify-customer' })
        return
      }

      const isToSkipRedirect = [
        'pdv-pay-box',
        'pdv-pay-box-configuration'
      ].some(t => t === this.$router.currentRoute.name)

      if (!isToSkipRedirect) {
        this.$router.push({ name: 'pdv-pay-box' })
      }
    },
    mxPayBoxOpenRouteRedirect() {
      // garantir que não pule a atualização do status do pdv
      this.resetPdvConfigurationLoaded()

      // evitar loop de redirecionamento
      const isToSkipRedirect = [
        'pdv-pay-box-open',
        'pdv-pay-box-configuration',
        'pdv-pay-box-inactive',
        'pdv-pay-box-unknown-error'
      ].some(t => t === this.$router.currentRoute.name)

      if (!isToSkipRedirect) {
        this.$router.push({ name: 'pdv-pay-box-open' })
      }
    },
    mxPayBoxConfigurationRouteRedirect() {
      if (this.$can('Setup', 'PayBox')) {
        this.$router.push({ name: 'pdv-pay-box-configuration' })
      } else {
        this.$router.push({ name: 'pdv-pay-box-inactive' })
      }
    },

    warmClients() {
      if (isElectronLocal && this.tefAgentConfigPayload.enabled) {
        window.electronAPI.tef
          .initialize({
            ...this.tefAgentConfigPayload,
            warmAction: true,
            restartClientMode: 'OnError',
          })
          .catch(err => console.error('Error warming clients', err))
      }
    },
    async checkUserStorePermission() {
      const payBoxPdvFilterStoreId =
        store.state.pages?.pdv?.payBoxConfiguration?.pdvGlobalFilters?.payBox?.store?.id

      if (payBoxPdvFilterStoreId && isPdvRoute() && !isUserAllowedStore(payBoxPdvFilterStoreId)) {
        this.mxRedirectWhenMount = false
        this.showError({
          title: this.$t('Configuração PDV'),
          message: this.$t(
            'Usuário não tem acesso para loja configurada para esta estação de trabalho. Entre em contato com o suporte.'
          ),
        })
        this.$router.push({ name: 'pdv-pay-box-unknown-error' })
      }
    },
    /**
     * Valida se o PDV está operante, caso não esteja, faz redirecionamento para não navegar pelas telas do PDV.
     */
    async checkPdvCurrentConfiguration() {
      if (!isElectronLocal) {
        return true
      }

      if (pdvConfigurationCheckPromise) {
        return pdvConfigurationCheckPromise
      }

      const clearCheckPromise = () => {
        setTimeout(() => {
          pdvConfigurationCheckPromise = null
        }, 1000)
      }

      pdvConfigurationCheckPromise = (async () => {
        try {
          // TODO: Devemos verificar se o TEF/SAT/NFCe foram `skipped`
          // caso a configuração foi realizada primeiro no ERP e depois aberto o PDV
          // todo
          const payboxData = await window.electronAPI.system.identifyPayboxRead()
          await this.checkPayBoxIsActiveByMacAddress(payboxData?.guid, this.macAddressWorkstation)
          return true
        } catch (error) {
          this.mxRedirectWhenMount = false

          // evitar loop de redirecionamentos
          const isToSkipRedirect = [
            'auth-reset-password' // tela de reset de senha (cenário que senha expirou)
          ].some(t => t === this.$router.currentRoute.name)

          if (isToSkipRedirect) {
            throw error
          }

          if (error instanceof PayBoxConnectionError) {
            // instabilidade de internet: mantém o usuário na tela atual em vez de forçar logout/tela de erro
            console.warn('PayBox connection check failed due to network instability, will retry later', error)
            this.showWarning({
              title: this.$t('Configuração PDV'),
              message: this.$t('Instabilidade na conexão com a internet. Tentando novamente em instantes.'),
            })
            return false
          }

          if (error instanceof PayBoxMissingSetupError) {
            if (this.$router.currentRoute.name !== 'pdv-pay-box-configuration') {
              this.showWarning({
                title: this.$t('Configuração PDV'),
                message: this.$t('PDV deve ser configurado corretamente'),
              })

              this.mxPayBoxConfigurationRouteRedirect()
            }
          } else if (error instanceof PayBoxInativeError) {
            this.showError({
              title: this.$t('Configuração PDV'),
              message: this.$t('Este PDV está desabilitado. Entre em contato com o suporte.'),
            })
            this.$router.push({ name: 'pdv-pay-box-inactive' })
          } else if (error instanceof PayBoxUnknownError) {
            this.showError({
              message: this.$t(
                'Ocorreu um erro inesperado ao buscar configurações do PDV. Tente novamente.'
              ),
            })
            this.$router.push({ name: 'pdv-pay-box-unknown-error' })
          }

          throw error
        }
      })()

      pdvConfigurationCheckPromise.then(clearCheckPromise, clearCheckPromise)
      return pdvConfigurationCheckPromise
    },

    /**
     * Reseta o flag de configuração carregada
     * Deve ser chamado no logout ou quando precisar forçar um novo carregamento
     */
    resetPdvConfigurationLoaded() {
      pdvConfigurationLoaded = false
      pdvDataLoaded = false
      pdvCashBookClosed = false
    },
  },
  async mounted() {
    const isPdv = isPdvRoute()
    try {
      if (isPdv) {
        this.showLoadingOverlay()
      }

      await this.loadPdvCurrentConfiguration(!this.hasGlobalFilters)

      const pdvConfigurationAvailable = await this.checkPdvCurrentConfiguration()
      if (!pdvConfigurationAvailable) {
        const currentRouteName = this.$router.currentRoute.name
        if (currentRouteName === 'pdv-pay-box-unknown-error') {
          return
        }
        
        const unavailableRoute = hasErpAccess() ? 'home' : 'pdv-pay-box-unknown-error'
        if (isPdv && currentRouteName !== unavailableRoute) {
          await this.$router.push({ name: unavailableRoute }).catch(() => {})
        }
        return
      }

      await this.checkUserStorePermission()
      
      if (isElectronLocal && this.isPdvConfigurationLoaded && !hasErpAccess() && this.isPdvInactive) {
        this.showError({
          title: this.$t('Configuração PDV'),
          message: this.$t('Este PDV foi inativado. Entre em contato com a administração.'),
        })
        this.$router.push({ name: 'pdv-pay-box-inactive' })
        return
      }

      if (isPdv) {
        await this.pageLoadPdv(false)

        if (this.isPdvAtm) {
          // Inicializa apenas os dados base do ATM (status, pagamentos, banners)
          // O catálogo será carregado quando entrar na rota pdv-atm
          await this.stInitAtmBase()
        }
      }

      this.warmClients()
    } finally {
      if (isPdv) {
        this.hideLoadingOverlay()
      }
    }

    if (isPdv && this.mxRedirectWhenMount) {
      this.mxPdvRouteRedirect()
    }
  },
}

const pdvBrowserNotAllowedConfiguration = {
  methods: {
    checkPdvOnBrowser() {
      if (isElectronLocal || !isProd) {
        return
      }
      this.showError({
        title: this.$t('Configuração PDV'),
        message: this.$t('Não é possível utilizar o PDV diretamente pelo browser.'),
      })

      console.info('Not Authorized PDV')
      this.$router.push({ name: 'misc-not-authorized' })
    },
  },
  async mounted() {
    this.checkPdvOnBrowser()
  },
}

export { payboxConfiguration as default, pdvBrowserNotAllowedConfiguration }
